import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase, getSignedUrl } from '../../lib/supabase';
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  X, 
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react';

// Componente para mostrar el ticket actual con URL firmada
const CurrentTicketDisplay = ({ ticketPath, filename, onRemove }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticketPath) {
      setLoading(false);
      return;
    }

    const fetchSignedUrl = async () => {
      try {
        setLoading(true);
        const url = await getSignedUrl('tickets', ticketPath);
        if (url) {
          setSignedUrl(url);
        }
      } catch (err) {
        console.error('Error cargando ticket:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [ticketPath]);

  if (loading) {
    return (
      <div className="border border-gray-300 rounded-lg p-4">
        <p className="text-sm text-gray-500">Cargando ticket...</p>
      </div>
    );
  }

  return (
    <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <FileText className="w-8 h-8 text-blue-600" />
        <div>
          <p className="text-sm font-medium text-gray-900">{filename || 'Ticket'}</p>
          {signedUrl && (
            <a
              href={signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Ver archivo
            </a>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

const TransactionEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [formData, setFormData] = useState({
    date: '',
    type: 'gasto',
    category: '',
    team: 'General',
    amount: '',
    description: '',
    notes: ''
  });

  const [currentTicket, setCurrentTicket] = useState(null);
  const [newTicketFile, setNewTicketFile] = useState(null);
  const [ticketPreview, setTicketPreview] = useState(null);

  const categories = {
    ingreso: ['Cuotas', 'Patrocinios', 'Subvenciones', 'Eventos', 'Donaciones', 'Otros Ingresos'],
    gasto: ['Material Deportivo', 'Equipaciones', 'Desplazamientos', 'Arbitraje', 'Mantenimiento', 'Seguros', 'Licencias', 'Otros Gastos']
  };

  const teams = ['General', 'Juvenil', 'Cadete', 'Amateur'];

  useEffect(() => {
    fetchTransaction();
  }, [id]);

  const fetchTransaction = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (!data) {
        navigate('/finance/transactions');
        return;
      }

      setFormData({
        date: data.date,
        type: data.type,
        category: data.category,
        team: data.team,
        amount: data.amount.toString(),
        description: data.description,
        notes: data.notes || ''
      });

      if (data.ticket_url) {
        setCurrentTicket({
          path: data.ticket_url,
          filename: data.ticket_filename || 'Ticket'
        });
      }
    } catch (error) {
      console.error('Error al cargar transacción:', error);
      setError('Error al cargar la transacción');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'type' && { category: '' })
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo no puede superar los 5MB');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Solo se permiten archivos JPG, PNG o PDF');
        return;
      }

      setNewTicketFile(file);
      setError('');

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setTicketPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setTicketPreview(null);
      }
    }
  };

  const removeNewFile = () => {
    setNewTicketFile(null);
    setTicketPreview(null);
  };

  const removeCurrentTicket = async () => {
    if (!currentTicket) return;

    try {
      await supabase.storage.from('tickets').remove([currentTicket.path]);
      setCurrentTicket(null);
    } catch (error) {
      console.error('Error al eliminar ticket:', error);
    }
  };

  // ✅ FUNCIÓN CORREGIDA PARA BUCKETS PRIVADOS
  const uploadTicket = async () => {
    if (!newTicketFile) return null;

    try {
      setUploadingFile(true);
      
      const fileExt = newTicketFile.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = fileName;

      const { error } = await supabase.storage
        .from('tickets')
        .upload(filePath, newTicketFile);

      if (error) throw error;

      // ✅ Solo devolvemos la ruta del archivo, no una URL pública
      return {
        path: filePath,
        filename: newTicketFile.name
      };
    } catch (error) {
      console.error('Error al subir ticket:', error);
      throw new Error('Error al subir el archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (!formData.category) {
        throw new Error('Debes seleccionar una categoría');
      }

      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        throw new Error('El monto debe ser mayor a 0');
      }

      let ticketData = {
        ticket_url: currentTicket?.path || null,
        ticket_filename: currentTicket?.filename || null
      };

      // Si hay nuevo ticket, eliminar el anterior y subir el nuevo
      if (newTicketFile) {
        if (currentTicket) {
          await removeCurrentTicket();
        }
        const uploaded = await uploadTicket();
        ticketData = {
          ticket_url: uploaded.path,
          ticket_filename: uploaded.filename
        };
      }

      const updateData = {
        date: formData.date,
        type: formData.type,
        category: formData.category,
        team: formData.team,
        amount: parseFloat(formData.amount),
        description: formData.description,
        notes: formData.notes,
        ...ticketData,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      setSuccess(true);
      
      setTimeout(() => {
        navigate('/finance/transactions');
      }, 2000);

    } catch (err) {
      console.error('Error al actualizar transacción:', err);
      setError(err.message || 'Error al actualizar la transacción');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando transacción...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Transacción actualizada!</h2>
          <p className="text-gray-600 mb-4">Los cambios se han guardado correctamente</p>
          <button
            onClick={() => navigate('/finance/transactions')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver a Transacciones
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/finance/transactions')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Editar Transacción</h1>
              <p className="text-sm text-gray-600">Modifica los datos de la transacción</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Fecha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="gasto">Gasto</option>
                <option value="ingreso">Ingreso</option>
              </select>
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoría <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecciona una categoría</option>
                {categories[formData.type].map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Equipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Equipo <span className="text-red-500">*</span>
              </label>
              <select
                name="team"
                value={formData.team}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>

            {/* Monto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monto (€) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                required
                min="0.01"
                step="0.01"
                placeholder="0.00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Descripción */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Notas */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas adicionales
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Ticket actual */}
            {currentTicket && !newTicketFile && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ticket actual
                </label>
                <CurrentTicketDisplay
                  ticketPath={currentTicket.path}
                  filename={currentTicket.filename}
                  onRemove={removeCurrentTicket}
                />
              </div>
            )}

            {/* Nuevo ticket */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {currentTicket ? 'Cambiar ticket' : 'Subir ticket'}
              </label>
              
              {!newTicketFile ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    id="ticket"
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="ticket" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="text-blue-600 font-medium">Click para subir</span> nuevo archivo
                    </p>
                    <p className="text-xs text-gray-500">
                      JPG, PNG o PDF (máx. 5MB)
                    </p>
                  </label>
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-start gap-4">
                    {ticketPreview ? (
                      <img 
                        src={ticketPreview} 
                        alt="Preview" 
                        className="w-20 h-20 object-cover rounded"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                        <FileText className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {newTicketFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(newTicketFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={removeNewFile}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Botones */}
          <div className="flex items-center justify-end gap-4 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/finance/transactions')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || uploadingFile}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {(saving || uploadingFile) && <Loader className="w-4 h-4 animate-spin" />}
              {uploadingFile ? 'Subiendo archivo...' : saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionEdit;